/**
 * AdminLegal.jsx — Hukuk & İcra Yönetim Ekranı
 *
 * Alt-sekmeler:
 *   1. İcra Dosyaları (legal-cases) — liste + detay modal
 *   2. İcraya Hazır Taksitler (ready-installments) — dosya açma
 *   3. Avukatlar (lawyers) — CRUD
 *
 * Backend commit: 4acc261
 * Tasarım: design_guidelines.json — Vibrant Play + Neumorphism Clay
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
// FAZ 4 PAKET I: icra ucunun `error_code` sozlugu (accounting.errors.*) senet
// ekranlariyla ORTAK — invalid_id_list / missing_debt_reference /
// senet_disi_taksit_karisimi / senet_sahiplik_uyusmuyor burada da cikar.
import { noteErrorMessage } from './AdminStudentHub/AccountingTab/noteUiKit';
import { fmtTRY } from '../utils/formatCurrency';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Scale,
  Plus,
  X,
  Eye,
  Upload,
  FileText,
  MessageSquare,
  CreditCard,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  User,
  Briefcase,
  Phone,
  Mail,
  Hash,
  Percent,
  Pencil,
  Trash2,
  Building2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * `/admin/legal/ready-installments` ucu taksitleri `{"_id": 0}` projeksiyonuyla
 * dondurur (services/legal_service.py::list_legal_ready_installments): kayitta
 * NE `_id` NE `id` alani vardir, kimlik YALNIZCA `installment_id`'dir.
 *
 * Onceki kod `i._id || i.id` okudugu icin secim listesi [undefined, ...] ile
 * doluyor, `POST /admin/legal-cases` gövdesine `installment_ids: [null, ...]`
 * gidiyordu. Backend eskiden bunu sessizce yutup "taksidi bagli olmayan,
 * borcu body'den gelen" bir icra dosyasi uretiyordu; FAZ 4 ile id listesi
 * dogrulaniyor (routes/legal.py::_str_id_list) ve akis 400 `invalid_id_list`
 * ile tamamen kiriliyordu.
 */
export function installmentKey(inst) {
  const id = inst?.installment_id;
  return typeof id === 'string' && id.trim() ? id.trim() : '';
}

/**
 * Ayni hata sinifinin ikinci kopyasi: icra dosyalarinin kimligi.
 *
 * `/admin/legal-cases` (liste) ve `/admin/legal-cases/{case_id}` (detay)
 * uclarinin ikisi de `{"_id": 0}` projeksiyonuyla doner
 * (services/legal_service.py::list_legal_cases / get_legal_case) — kayitta NE
 * `_id` NE `id` vardir, kimlik YALNIZCA `case_id`'dir
 * (legal_service.py::create_legal_case, `case_id = f"lc_{...}"`).
 *
 * Onceki kod `c._id || c.id` okudugu icin detay/statu/not/belge/tahsilat/ihtar
 * istekleri `/admin/legal-cases/undefined` adresine gidiyordu: Icra Dosyalari
 * sekmesi buyuk olcude calismiyordu.
 */
export function legalCaseKey(legalCase) {
  const id = legalCase?.case_id;
  return typeof id === 'string' && id.trim() ? id.trim() : '';
}

/**
 * B9 duzeltmesi: EKRANA BASILAN dosya numarasi `case_id` DEGIL `case_no`.
 *
 * `case_id` (`lc_` + uuid4 hex[:12], legal_service.py:406) ic sistem
 * kimligidir — eskiden ekranda son 8 karakteri (`819878F3` gibi) turetilerek
 * gosteriliyordu. `case_no` (`ICR-YYYY-NNNNNN`) ise atomik Mongo counter'dan
 * uretilen (legal_service.py:1172-1188, `db.counters` + `$inc`/`upsert`),
 * yil-sira tasiyan RESMI belge numarasidir; ihtarname PDF'inde "Dosya No"
 * olarak basilir (legal_service.py:904) ve backend'in kendi dosya adi
 * sozlesmesi de bunu kullanir (routes/legal.py:987-988, `ihtar_{case_no}.pdf`).
 * Operator PDF'te gordugu numarayla ekranda gordugu numara AYNI olmali.
 *
 * `case_no` `/admin/legal-cases` (liste) ve `/admin/legal-cases/{case_id}`
 * (detay) uclarinin ikisinden de gelir — projeksiyon `{"_id": 0}` disinda
 * alan filtrelemiyor (list_legal_cases/get_legal_case), backend degisikligi
 * gerekmedi.
 *
 * Fallback: `case_no` alani eklenmeden ONCE acilmis olabilecek eski
 * kayitlarda bu alan olmayabilir (geriye donuk backfill migration'i yok).
 * Boyle bir kayitta eski ekranin gosterdigi deger (case_id'nin son 8
 * karakteri) korunur — TAMAMEN bos gostermek yerine kullaniciya en azindan
 * BIR ayirt edici deger sunulur. Hicbir kimlik yoksa bos string doner,
 * cagiran '—' basar.
 */
export function legalCaseNo(legalCase) {
  const no = legalCase?.case_no;
  if (typeof no === 'string' && no.trim()) return no.trim();
  const id = legalCaseKey(legalCase);
  return id ? id.slice(-8).toUpperCase() : '';
}

/**
 * Ucuncu kopya: avukat kimligi.
 *
 * `/admin/lawyers` ucu da `{"_id": 0}` projeksiyonuyla doner
 * (services/lawyer_service.py::list_lawyers) — kimlik YALNIZCA `lawyer_id`
 * (`lawyer_id = f"law_{...}"`, lawyer_service.py::create_lawyer).
 *
 * `l._id || l.id` `undefined` verdigi icin React `<option value>`'u atliyor ve
 * secenegin degeri AVUKATIN ADI oluyordu. Backend `lawyer_id`'nin varligini
 * dogrulamiyor (legal_service.py::create_legal_case dogrudan yaziyor, ardindan
 * increment_active_cases cagiriyor) → dosya `lawyer_id: "Av. Ahmet"` gibi bir
 * degerle acilip var olmayan bir kimlige sayac yaziliyordu: SESSIZ BOZUK VERI.
 */
export function lawyerKey(lawyer) {
  const id = lawyer?.lawyer_id;
  return typeof id === 'string' && id.trim() ? id.trim() : '';
}

/** Kimligi cozulebilen avukatlar — secicilerde YALNIZCA bunlar gosterilir. */
export function selectableLawyers(lawyers) {
  return (lawyers || []).filter((l) => lawyerKey(l));
}

/**
 * Dorduncu kopya: taksit sira numarasi.
 *
 * Backend alan adi `installment_no` (services/sales_service.py:763/828/909);
 * ekran `installment_number || order` okudugu icin kolon her satirda "—"
 * gosteriyordu. 0 gecerli bir sira degeri degil, ama falsy tuzagina
 * dusulmemesi icin tip kontrolu ile okunur.
 */
export function installmentNo(inst) {
  const no = inst?.installment_no;
  if (typeof no === 'number' && Number.isFinite(no)) return String(no);
  if (typeof no === 'string' && no.trim()) return no.trim();
  return '';
}

/**
 * `POST /admin/legal-cases` govdesi.
 *
 * `debt_amount` BILEREK GONDERILMEZ: backend borcu kaynak kayittan turetir
 * (routes/legal.py — "body debt_amount YOK SAYILDI, borc taksitlerden
 * turetildi", PLAN §6.2/1). Gonderilse bile yok sayilacagi icin ekranda da
 * duzenlenebilir bir tutar alani yoktur; gosterilen toplam yalnizca
 * bilgilendirmedir ve backend'in turettigi degerle ayni kaynaktan gelir.
 */
export function buildLegalCasePayload(installments, form) {
  const first = installments?.[0] || {};
  return {
    user_id: first.user_id,
    invoice_id: first.invoice_id || first.invoice || null,
    installment_ids: (installments || []).map(installmentKey).filter(Boolean),
    interest_amount: parseFloat(form?.interest_amount) || 0,
    legal_fees: parseFloat(form?.legal_fees) || 0,
    lawyer_id: form?.lawyer_id || null,
    notes: form?.notes || null,
  };
}

// ── Yardımcı: tarih biçimlendirme ────────────────────────────────────────────
function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('tr-TR');
  } catch {
    return val;
  }
}

// ── Durum rozeti renkleri ─────────────────────────────────────────────────────
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  notice_sent: 'bg-blue-100 text-blue-700 border-blue-200',
  legal_started: 'bg-purple-100 text-purple-700 border-purple-200',
  payment_order: 'bg-orange-100 text-orange-700 border-orange-200',
  seizure: 'bg-rose-100 text-rose-700 border-rose-200',
  collected: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

function StatusBadge({ status, t }) {
  const color = STATUS_COLORS[status] || 'bg-slate-100 text-slate-500 border-slate-200';
  const label = t(`admin.legal.status.${status}`) || status;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold border ${color}`}>
      {label}
    </span>
  );
}

// ── Sekme butonları ───────────────────────────────────────────────────────────
function TabBar({ tab, setTab, t }) {
  const tabs = [
    { id: 'cases', labelKey: 'admin.legal.tab.cases', icon: Scale },
    { id: 'ready', labelKey: 'admin.legal.tab.ready', icon: Clock },
    { id: 'lawyers', labelKey: 'admin.legal.tab.lawyers', icon: Briefcase },
  ];
  return (
    <div className="flex gap-1 p-1 bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] mb-6 w-fit">
      {tabs.map(({ id, labelKey, icon: Icon }) => (
        <button
          key={id}
          onClick={() => setTab(id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
            tab === id
              ? 'bg-[var(--brand-primary)] text-white shadow-sm'
              : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
          }`}
        >
          <Icon size={16} />
          {t(labelKey)}
        </button>
      ))}
    </div>
  );
}

// ── Loading / Empty / Error states ───────────────────────────────────────────
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
    </div>
  );
}

function EmptyState({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <div className="w-14 h-14 rounded-2xl bg-[var(--bg-muted)] flex items-center justify-center">
        <Icon size={24} className="text-[var(--text-muted)]" />
      </div>
      <p className="font-bold text-[var(--text-main)]">{title}</p>
      {sub && <p className="text-sm text-[var(--text-muted)]">{sub}</p>}
    </div>
  );
}

function ErrorState({ msg, onRetry, t }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <AlertTriangle size={32} className="text-rose-500" />
      <p className="font-bold text-rose-600">{msg}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-colors"
        >
          {t('common.retry') || 'Tekrar Dene'}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SEKME 1: İcra Dosyaları
// ─────────────────────────────────────────────────────────────────────────────

function CasesTab({ t, lang, authHeaders }) {
  const toast = useToast();
  const [cases, setCases] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLawyer, setFilterLawyer] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 20;
  const [selectedCase, setSelectedCase] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (filterStatus) params.set('status', filterStatus);
      if (filterLawyer) params.set('lawyer_id', filterLawyer);
      const res = await axios.get(`${API}/admin/legal-cases?${params}`, { headers: authHeaders() });
      setCases(res.data.legal_cases || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [skip, filterStatus, filterLawyer, lang, authHeaders]);

  const fetchLawyers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/lawyers?limit=200`, { headers: authHeaders() });
      setLawyers(res.data.lawyers || []);
    } catch {
      // sessiz
    }
  }, [authHeaders]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { fetchLawyers(); }, [fetchLawyers]);

  // Kimligi cozulemeyen dosya icin istek HIC gitmez: `/admin/legal-cases/undefined`
  // 404 doner ve kullaniciya "dosya bulunamadi" diye yanlis bir teshis yedirirdi.
  const openDetail = async (legalCase) => {
    const caseId = legalCaseKey(legalCase);
    if (!caseId) {
      toast.error(t('admin.legal.cases.idMissing'));
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/legal-cases/${caseId}`, { headers: authHeaders() });
      setSelectedCase(res.data);
      setDetailOpen(true);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedCase(null);
  };

  const validStatuses = ['pending', 'notice_sent', 'legal_started', 'payment_order', 'seizure', 'collected', 'closed'];

  return (
    <div>
      {/* Filtreler */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setSkip(0); }}
          className="px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm font-semibold text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
          aria-label={t('admin.legal.filter.status')}
        >
          <option value="">{t('admin.legal.filter.allStatuses')}</option>
          {validStatuses.map(s => (
            <option key={s} value={s}>{t(`admin.legal.status.${s}`)}</option>
          ))}
        </select>
        <select
          value={filterLawyer}
          onChange={e => { setFilterLawyer(e.target.value); setSkip(0); }}
          className="px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm font-semibold text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
          aria-label={t('admin.legal.filter.lawyer')}
        >
          <option value="">{t('admin.legal.filter.allLawyers')}</option>
          {selectableLawyers(lawyers).map(l => (
            <option key={lawyerKey(l)} value={lawyerKey(l)}>{l.name}</option>
          ))}
        </select>
      </div>

      {/* Liste */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState msg={error} onRetry={fetchCases} t={t} />
      ) : cases.length === 0 ? (
        <EmptyState icon={Scale} title={t('admin.legal.cases.empty')} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-[var(--border)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-muted)]">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.caseNo')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.student')}</th>
                  <th className="px-4 py-3 text-right font-bold text-[var(--text-muted)]">{t('admin.legal.col.totalDebt')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.status')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.lawyer')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.date')}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {cases.map((c, idx) => {
                  const caseId = legalCaseKey(c);
                  const missingId = !caseId;
                  const missingIdHintId = `legal-case-id-missing-${idx}`;
                  return (
                  <tr key={caseId || `row-${idx}`} className="bg-[var(--bg-card)] hover:bg-[var(--bg-muted)] transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-[var(--text-muted)]">
                      {legalCaseNo(c) || '—'}
                    </td>
                    <td className="px-4 py-3 font-semibold text-[var(--text-main)]">
                      {c.student_name || c.user_id || '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-rose-600">
                      {fmtTRY(c.total_amount || (c.debt_amount || 0) + (c.interest_amount || 0) + (c.legal_fees || 0))}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} t={t} />
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)]">
                      {c.lawyer_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)]">
                      {fmtDate(c.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {missingId && (
                          <span
                            role="alert"
                            id={missingIdHintId}
                            data-testid="legal-case-id-error"
                            className="flex items-center gap-1 text-[11px] font-semibold text-rose-700"
                          >
                            <AlertTriangle size={13} className="flex-shrink-0" aria-hidden />
                            {t('admin.legal.cases.idMissing')}
                          </span>
                        )}
                        <button
                          onClick={() => openDetail(c)}
                          disabled={missingId}
                          data-testid="legal-case-detail-btn"
                          aria-describedby={missingId ? missingIdHintId : undefined}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-[var(--brand-primary)] text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                          aria-label={t('admin.legal.btn.detail')}
                        >
                          <Eye size={14} />
                          {t('admin.legal.btn.detail')}
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          <div className="flex items-center justify-between mt-4 text-sm text-[var(--text-muted)]">
            <span>{t('admin.legal.pagination').replace('{from}', skip + 1).replace('{to}', Math.min(skip + limit, total)).replace('{total}', total)}</span>
            <div className="flex gap-2">
              <button
                disabled={skip === 0}
                onClick={() => setSkip(s => Math.max(0, s - limit))}
                className="px-3 py-1.5 rounded-xl border border-[var(--border)] font-bold disabled:opacity-40 hover:bg-[var(--bg-muted)] transition-colors"
              >
                {t('admin.legal.prev')}
              </button>
              <button
                disabled={skip + limit >= total}
                onClick={() => setSkip(s => s + limit)}
                className="px-3 py-1.5 rounded-xl border border-[var(--border)] font-bold disabled:opacity-40 hover:bg-[var(--bg-muted)] transition-colors"
              >
                {t('admin.legal.next')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Detay Modal */}
      <AnimatePresence>
        {detailOpen && selectedCase && (
          <CaseDetailModal
            legalCase={selectedCase}
            onClose={closeDetail}
            onRefresh={() => { fetchCases(); openDetail(selectedCase); }}
            lawyers={lawyers}
            t={t}
            lang={lang}
            authHeaders={authHeaders}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── İcra Dosyası Detay Modalı ─────────────────────────────────────────────────
export function CaseDetailModal({ legalCase: initialCase, onClose, onRefresh, lawyers: _lawyers, t, lang, authHeaders }) {
  const toast = useToast();
  const [lc, setLc] = useState(initialCase);
  const [activeSection, setActiveSection] = useState('overview');
  const [saving, setSaving] = useState(false);

  // Dosya kimligi TEK yerden okunur; `/admin/legal-cases/undefined` uretecek
  // hicbir yol kalmasin diye tum yazma islemleri bu degere bagli.
  const caseId = legalCaseKey(lc);
  const idMissing = !caseId;
  const idMissingHintId = 'legal-case-detail-id-missing';

  // Durum güncelleme
  const [newStatus, setNewStatus] = useState('');
  const statusOrder = ['pending', 'notice_sent', 'legal_started', 'payment_order', 'seizure', 'collected', 'closed'];
  const currentIdx = statusOrder.indexOf(lc.status);
  const nextStatuses = statusOrder.slice(currentIdx + 1);

  const updateStatus = async () => {
    if (!newStatus || idMissing) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/legal-cases/${caseId}/status`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      setLc(prev => ({ ...prev, status: res.data.status || newStatus }));
      setNewStatus('');
      toast.success(t('admin.legal.msg.statusUpdated'));
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(false);
    }
  };

  // Not ekleme
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const addNote = async () => {
    if (!noteText.trim() || idMissing) return;
    setSavingNote(true);
    try {
      await axios.post(
        `${API}/admin/legal-cases/${caseId}/notes`,
        { text: noteText.trim() },
        { headers: authHeaders() }
      );
      setNoteText('');
      toast.success(t('admin.legal.msg.noteAdded'));
      // Yenile
      const res = await axios.get(`${API}/admin/legal-cases/${caseId}`, { headers: authHeaders() });
      setLc(res.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSavingNote(false);
    }
  };

  // Belge yükleme
  const [docType, setDocType] = useState('');
  const [docDesc, setDocDesc] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileRef = useRef(null);

  const uploadDocument = async (file) => {
    if (!file || idMissing) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      if (docType) fd.append('document_type', docType);
      if (docDesc) fd.append('description', docDesc);
      // Content-Type multipart — otomatik, elle set edilmiyor
      await axios.post(`${API}/admin/legal-cases/${caseId}/documents`, fd, { headers: authHeaders() });
      toast.success(t('admin.legal.msg.docUploaded'));
      setDocType(''); setDocDesc('');
      const res = await axios.get(`${API}/admin/legal-cases/${caseId}`, { headers: authHeaders() });
      setLc(res.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setUploadingDoc(false);
    }
  };

  // Tahsilat ekleme
  const [payForm, setPayForm] = useState({ amount: '', payment_date: new Date().toISOString().split('T')[0], method: 'banka_havalesi', notes: '' });
  const [savingPay, setSavingPay] = useState(false);

  const addPayment = async () => {
    if (idMissing) return;
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) {
      toast.warning(t('admin.legal.msg.invalidAmount'));
      return;
    }
    setSavingPay(true);
    try {
      await axios.post(
        `${API}/admin/legal-cases/${caseId}/payments`,
        { amount, payment_date: payForm.payment_date, method: payForm.method, notes: payForm.notes },
        { headers: authHeaders() }
      );
      setPayForm({ amount: '', payment_date: new Date().toISOString().split('T')[0], method: 'banka_havalesi', notes: '' });
      toast.success(t('admin.legal.msg.paymentAdded'));
      const res = await axios.get(`${API}/admin/legal-cases/${caseId}`, { headers: authHeaders() });
      setLc(res.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSavingPay(false);
    }
  };

  // İhtar PDF
  const downloadNoticePdf = async () => {
    if (idMissing) return;
    try {
      const res = await axios.get(`${API}/admin/legal-cases/${caseId}/notice-pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      // B9 devam: blob: URL'den indirilen dosyanin adini backend'in
      // Content-Disposition header'i BELIRLEMEZ — bu blob, XHR yanitindan
      // ayri, bellekte JS ile kurulmus yeni bir nesne (satir 624:
      // `new Blob([res.data], ...)`); tarayici header'i degil YALNIZCA
      // anchor'in `download` ozniteligini kullanir. O yuzden tek dogruluk
      // kaynagi burada ekranda gosterilen ayni fonksiyon: `legalCaseNo(lc)`
      // (bkz. satir 113) — case_no varsa onu, yoksa ekrandaki fallback'i
      // (case_id'nin son 8 karakteri, buyuk harf) kullanir. Onceki kod
      // `lc.case_no || caseId` idi: case_no eksikse ekranda "819878F3"
      // gosterip dosyayi ham `lc_a1b2c3819878f3.pdf` adiyla indiriyordu.
      a.download = `ihtar_${legalCaseNo(lc) || caseId}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const totalDebt = (lc.debt_amount || 0) + (lc.interest_amount || 0) + (lc.legal_fees || 0);
  const totalCollected = (lc.payments || []).reduce((s, p) => s + (p.amount || 0), 0);

  const sections = [
    { id: 'overview', label: t('admin.legal.detail.overview'), icon: Scale },
    { id: 'docs', label: t('admin.legal.detail.docs'), icon: FileText },
    { id: 'notes', label: t('admin.legal.detail.notes'), icon: MessageSquare },
    { id: 'payments', label: t('admin.legal.detail.payments'), icon: CreditCard },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t('admin.legal.detail.title')}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl"
      >
        {/* Modal başlık */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <Scale size={18} className="text-rose-600" />
            </div>
            <div>
              <h2 className="font-display font-bold text-[var(--text-main)]">
                {t('admin.legal.detail.title')}
              </h2>
              <p className="text-xs text-[var(--text-muted)]">
                {legalCaseNo(lc) || '—'} · {lc.student_name || '—'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadNoticePdf}
              disabled={idMissing}
              data-testid="legal-detail-notice-pdf"
              aria-describedby={idMissing ? idMissingHintId : undefined}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-100 text-amber-700 text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-200 transition-colors border border-amber-200"
              aria-label={t('admin.legal.btn.downloadNotice')}
            >
              <Download size={14} />
              {t('admin.legal.btn.downloadNotice')}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-[var(--text-muted)] hover:bg-[var(--bg-muted)] transition-colors"
              aria-label={t('common.close') || 'Kapat'}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* İç sekme barı */}
        <div className="flex gap-1 px-6 pt-3 flex-shrink-0 overflow-x-auto">
          {sections.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                activeSection === id
                  ? 'bg-[var(--brand-primary)] text-white'
                  : 'text-[var(--text-muted)] hover:bg-[var(--bg-muted)]'
              }`}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>

        {/* İçerik */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* Kimlik cozulemedi: TUM yazma islemleri kapali, gerekce ekranda. */}
          {idMissing && (
            <div
              role="alert"
              id={idMissingHintId}
              data-testid="legal-detail-id-error"
              className="mb-4 flex items-start gap-2 rounded-xl border-2 border-rose-300 bg-rose-50 p-3 text-xs font-semibold text-rose-800"
            >
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" aria-hidden />
              {t('admin.legal.detail.idMissing')}
            </div>
          )}

          {/* ─ Genel Bakış ─ */}
          {activeSection === 'overview' && (
            <div className="space-y-5">
              {/* Durum + rozet */}
              <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge status={lc.status} t={t} />
                <span className="text-sm text-[var(--text-muted)]">
                  {t('admin.legal.col.lawyer')}: <span className="font-semibold text-[var(--text-main)]">{lc.lawyer_name || '—'}</span>
                </span>
              </div>

              {/* Borç dökümü */}
              <div className="bg-[var(--bg-muted)] rounded-2xl p-4 space-y-2">
                <h3 className="font-bold text-sm text-[var(--text-main)] mb-3">{t('admin.legal.detail.debtBreakdown')}</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-[var(--text-muted)]">{t('admin.legal.debt.principal')}</span>
                  <span className="font-bold">{fmtTRY(lc.debt_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[var(--text-muted)]">{t('admin.legal.debt.interest')}</span>
                  <span className="font-bold">{fmtTRY(lc.interest_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[var(--text-muted)]">{t('admin.legal.debt.fees')}</span>
                  <span className="font-bold">{fmtTRY(lc.legal_fees)}</span>
                </div>
                <div className="border-t border-[var(--border)] pt-2 flex justify-between text-sm font-bold">
                  <span className="text-[var(--text-main)]">{t('admin.legal.debt.total')}</span>
                  <span className="text-rose-600 text-base">{fmtTRY(totalDebt)}</span>
                </div>
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>{t('admin.legal.debt.collected')}</span>
                  <span className="font-bold">{fmtTRY(totalCollected)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold border-t border-[var(--border)] pt-2">
                  <span className="text-[var(--text-muted)]">{t('admin.legal.debt.remaining')}</span>
                  <span className="text-amber-600">{fmtTRY(Math.max(0, totalDebt - totalCollected))}</span>
                </div>
              </div>

              {/* Durum güncelle */}
              {nextStatuses.length > 0 && (
                <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-4">
                  <h3 className="font-bold text-sm text-[var(--text-main)] mb-3">{t('admin.legal.detail.updateStatus')}</h3>
                  <div className="flex gap-2 flex-wrap">
                    <select
                      value={newStatus}
                      onChange={e => setNewStatus(e.target.value)}
                      className="flex-1 min-w-[160px] px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm font-semibold text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                      aria-label={t('admin.legal.detail.updateStatus')}
                      id="status-select"
                    >
                      <option value="">{t('admin.legal.filter.selectStatus')}</option>
                      {nextStatuses.map(s => (
                        <option key={s} value={s}>{t(`admin.legal.status.${s}`)}</option>
                      ))}
                    </select>
                    <button
                      onClick={updateStatus}
                      disabled={!newStatus || saving || idMissing}
                      data-testid="legal-detail-status-submit"
                      aria-describedby={idMissing ? idMissingHintId : undefined}
                      className="px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold disabled:opacity-40 hover:opacity-90 transition-opacity flex items-center gap-1.5"
                    >
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                      {t('admin.legal.btn.updateStatus')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─ Belgeler ─ */}
          {activeSection === 'docs' && (
            <div className="space-y-4">
              {/* Yükleme formu */}
              <div className="bg-[var(--bg-muted)] rounded-2xl p-4 space-y-3">
                <h3 className="font-bold text-sm text-[var(--text-main)]">{t('admin.legal.detail.uploadDoc')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="doc-type" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                      {t('admin.legal.doc.type')}
                    </label>
                    <input
                      id="doc-type"
                      value={docType}
                      onChange={e => setDocType(e.target.value)}
                      placeholder={t('admin.legal.doc.typePlaceholder')}
                      className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    />
                  </div>
                  <div>
                    <label htmlFor="doc-desc" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                      {t('admin.legal.doc.desc')}
                    </label>
                    <input
                      id="doc-desc"
                      value={docDesc}
                      onChange={e => setDocDesc(e.target.value)}
                      placeholder={t('admin.legal.doc.descPlaceholder')}
                      className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    />
                  </div>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  aria-label={t('admin.legal.doc.selectFile')}
                  onChange={e => uploadDocument(e.target.files[0])}
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingDoc || idMissing}
                  data-testid="legal-detail-doc-submit"
                  aria-describedby={idMissing ? idMissingHintId : undefined}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold disabled:opacity-40 hover:opacity-90 transition-opacity"
                >
                  {uploadingDoc ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {t('admin.legal.btn.uploadDoc')}
                </button>
              </div>

              {/* Belge listesi */}
              {(lc.documents || []).length === 0 ? (
                <EmptyState icon={FileText} title={t('admin.legal.docs.empty')} />
              ) : (
                <ul className="space-y-2">
                  {(lc.documents || []).map((doc, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] bg-[var(--bg-card)]">
                      <FileText size={16} className="text-[var(--brand-primary)] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-[var(--text-main)] truncate">{doc.filename || doc.document_type || `Belge ${i + 1}`}</p>
                        {doc.description && <p className="text-xs text-[var(--text-muted)]">{doc.description}</p>}
                        <p className="text-xs text-[var(--text-muted)]">{fmtDate(doc.uploaded_at)}</p>
                      </div>
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors"
                          aria-label={t('admin.legal.btn.viewDoc')}
                        >
                          <Eye size={16} />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ─ Notlar ─ */}
          {activeSection === 'notes' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="note-text" className="text-xs font-semibold text-[var(--text-muted)] block">
                  {t('admin.legal.note.label')}
                </label>
                <textarea
                  id="note-text"
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  rows={3}
                  placeholder={t('admin.legal.note.placeholder')}
                  className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                />
                <button
                  onClick={addNote}
                  disabled={!noteText.trim() || savingNote || idMissing}
                  data-testid="legal-detail-note-submit"
                  aria-describedby={idMissing ? idMissingHintId : undefined}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold disabled:opacity-40 hover:opacity-90 transition-opacity"
                >
                  {savingNote ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {t('admin.legal.btn.addNote')}
                </button>
              </div>

              {(lc.notes || []).length === 0 ? (
                <EmptyState icon={MessageSquare} title={t('admin.legal.notes.empty')} />
              ) : (
                <ul className="space-y-2">
                  {(lc.notes || []).map((note, i) => (
                    <li key={i} className="p-3 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)]">
                      <p className="text-sm text-[var(--text-main)]">{note.text}</p>
                      <p className="text-xs text-[var(--text-muted)] mt-1">{fmtDate(note.created_at)}{note.author_name ? ` · ${note.author_name}` : ''}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ─ Tahsilatlar ─ */}
          {activeSection === 'payments' && (
            <div className="space-y-4">
              <div className="bg-[var(--bg-muted)] rounded-2xl p-4 space-y-3">
                <h3 className="font-bold text-sm text-[var(--text-main)]">{t('admin.legal.detail.addPayment')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="pay-amount" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                      {t('admin.legal.pay.amount')}
                    </label>
                    <input
                      id="pay-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={payForm.amount}
                      onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    />
                  </div>
                  <div>
                    <label htmlFor="pay-date" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                      {t('admin.legal.pay.date')}
                    </label>
                    <input
                      id="pay-date"
                      type="date"
                      value={payForm.payment_date}
                      onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    />
                  </div>
                  <div>
                    <label htmlFor="pay-method" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                      {t('admin.legal.pay.method')}
                    </label>
                    <select
                      id="pay-method"
                      value={payForm.method}
                      onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    >
                      <option value="banka_havalesi">{t('admin.legal.pay.method.transfer')}</option>
                      <option value="nakit">{t('admin.legal.pay.method.cash')}</option>
                      <option value="kredi_karti">{t('admin.legal.pay.method.card')}</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="pay-notes" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                      {t('admin.legal.pay.notes')}
                    </label>
                    <input
                      id="pay-notes"
                      value={payForm.notes}
                      onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder={t('admin.legal.pay.notesPlaceholder')}
                      className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    />
                  </div>
                </div>
                <button
                  onClick={addPayment}
                  disabled={savingPay || idMissing}
                  data-testid="legal-detail-payment-submit"
                  aria-describedby={idMissing ? idMissingHintId : undefined}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 text-white text-sm font-bold disabled:opacity-40 hover:bg-emerald-600 transition-colors"
                >
                  {savingPay ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {t('admin.legal.btn.addPayment')}
                </button>
              </div>

              {(lc.payments || []).length === 0 ? (
                <EmptyState icon={CreditCard} title={t('admin.legal.payments.empty')} />
              ) : (
                <ul className="space-y-2">
                  {(lc.payments || []).map((pay, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] bg-[var(--bg-card)]">
                      <CreditCard size={16} className="text-emerald-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-emerald-600">{fmtTRY(pay.amount)}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {fmtDate(pay.payment_date)} · {pay.method}
                          {pay.notes ? ` · ${pay.notes}` : ''}
                        </p>
                      </div>
                    </li>
                  ))}
                  <li className="flex justify-between px-3 pt-2 border-t border-[var(--border)] text-sm font-bold">
                    <span className="text-[var(--text-muted)]">{t('admin.legal.debt.collected')}</span>
                    <span className="text-emerald-600">{fmtTRY(totalCollected)}</span>
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SEKME 2: İcraya Hazır Taksitler
// ─────────────────────────────────────────────────────────────────────────────

function ReadyInstallmentsTab({ t, lang, authHeaders }) {
  const [installments, setInstallments] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const fetchInstallments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/legal/ready-installments?skip=${skip}&limit=${limit}`, { headers: authHeaders() });
      setInstallments(res.data.installments || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [skip, lang, authHeaders]);

  const fetchLawyers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/lawyers?limit=200`, { headers: authHeaders() });
      setLawyers(res.data.lawyers || []);
    } catch {
      // sessiz
    }
  }, [authHeaders]);

  useEffect(() => { fetchInstallments(); }, [fetchInstallments]);
  useEffect(() => { fetchLawyers(); }, [fetchLawyers]);

  const toggleSelect = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const toggleAll = () => {
    if (selected.length === installments.length) setSelected([]);
    else setSelected(installments.map(installmentKey).filter(Boolean));
  };

  const selectedInstallments = installments.filter(i => selected.includes(installmentKey(i)));

  return (
    <div>
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState msg={error} onRetry={fetchInstallments} t={t} />
      ) : installments.length === 0 ? (
        <EmptyState icon={Clock} title={t('admin.legal.ready.empty')} sub={t('admin.legal.ready.emptySub')} />
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[var(--text-muted)]">
              {selected.length > 0
                ? t('admin.legal.ready.selectedCount').replace('{n}', selected.length)
                : t('admin.legal.ready.hint')}
            </p>
            {selected.length > 0 && (
              <button
                onClick={() => setOpenModal(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-colors"
              >
                <Scale size={14} />
                {t('admin.legal.btn.openCase')}
              </button>
            )}
          </div>

          <div className="overflow-x-auto rounded-2xl border border-[var(--border)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-muted)]">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selected.length === installments.length && installments.length > 0}
                      onChange={toggleAll}
                      aria-label={t('admin.legal.ready.selectAll')}
                      className="rounded border-[var(--border)]"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.student')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.installment')}</th>
                  <th className="px-4 py-3 text-right font-bold text-[var(--text-muted)]">{t('admin.legal.col.amount')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.dueDate')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.col.overdueDays')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {installments.map(inst => {
                  const id = installmentKey(inst);
                  const isSelected = selected.includes(id);
                  return (
                    <tr
                      key={id}
                      className={`transition-colors cursor-pointer ${isSelected ? 'bg-rose-50' : 'bg-[var(--bg-card)] hover:bg-[var(--bg-muted)]'}`}
                      onClick={() => toggleSelect(id)}
                    >
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(id)}
                          aria-label={`${t('admin.legal.ready.selectRow')} ${inst.student_name || id}`}
                          className="rounded border-[var(--border)]"
                        />
                      </td>
                      <td className="px-4 py-3 font-semibold text-[var(--text-main)]">{inst.student_name || inst.user_id || '—'}</td>
                      <td className="px-4 py-3 text-[var(--text-muted)]">#{installmentNo(inst) || '—'}</td>
                      <td className="px-4 py-3 text-right font-bold text-rose-600">{fmtTRY(inst.amount)}</td>
                      <td className="px-4 py-3 text-[var(--text-muted)]">{fmtDate(inst.due_date)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-lg bg-rose-100 text-rose-700 text-xs font-bold border border-rose-200">
                          {inst.overdue_days ?? '—'} {t('admin.legal.ready.days')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-[var(--text-muted)]">
            <span>{t('admin.legal.pagination').replace('{from}', skip + 1).replace('{to}', Math.min(skip + limit, total)).replace('{total}', total)}</span>
            <div className="flex gap-2">
              <button disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - limit))} className="px-3 py-1.5 rounded-xl border border-[var(--border)] font-bold disabled:opacity-40 hover:bg-[var(--bg-muted)] transition-colors">{t('admin.legal.prev')}</button>
              <button disabled={skip + limit >= total} onClick={() => setSkip(s => s + limit)} className="px-3 py-1.5 rounded-xl border border-[var(--border)] font-bold disabled:opacity-40 hover:bg-[var(--bg-muted)] transition-colors">{t('admin.legal.next')}</button>
            </div>
          </div>
        </>
      )}

      <AnimatePresence>
        {openModal && (
          <OpenCaseModal
            installments={selectedInstallments}
            lawyers={lawyers}
            onClose={() => setOpenModal(false)}
            onSuccess={() => { setOpenModal(false); setSelected([]); fetchInstallments(); }}
            t={t}
            lang={lang}
            authHeaders={authHeaders}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── İcra Dosyası Açma Modalı ─────────────────────────────────────────────────
export function OpenCaseModal({ installments, lawyers, onClose, onSuccess, t, lang, authHeaders }) {
  const toast = useToast();
  const [form, setForm] = useState({
    interest_amount: '',
    legal_fees: '',
    lawyer_id: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const totalDebt = installments.reduce((s, i) => s + (i.amount || 0), 0);

  // Kimligi cozulemeyen avukat seceneğe DONUSTURULMEZ: aksi halde `<option>`
  // degeri avukatin ADI olur ve backend bu degeri dogrulamadan `lawyer_id`
  // olarak yazar (sessiz bozuk veri). Dusen kayit varsa gerekce ekranda.
  const lawyerOptions = selectableLawyers(lawyers);
  const lawyersDropped = (lawyers || []).length - lawyerOptions.length;

  // Kimligi cozulemeyen taksitle istek GONDERILMEZ: backend 400
  // `invalid_id_list` dondururdu, kullaniciya anlamsiz bir hata yedirilir.
  const payload = buildLegalCasePayload(installments, form);
  const idsMissing = payload.installment_ids.length !== installments.length;

  const submit = async () => {
    if (installments.length === 0 || idsMissing) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/legal-cases`, payload, { headers: authHeaders() });
      toast.success(t('admin.legal.msg.caseOpened'));
      onSuccess();
    } catch (err) {
      toast.error(noteErrorMessage(err, lang, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t('admin.legal.openCase.title')}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] w-full max-w-md shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display font-bold text-[var(--text-main)]">{t('admin.legal.openCase.title')}</h2>
          <button onClick={onClose} className="p-2 rounded-xl text-[var(--text-muted)] hover:bg-[var(--bg-muted)] transition-colors" aria-label={t('common.close') || 'Kapat'}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Seçili taksitler özeti */}
          <div className="bg-[var(--bg-muted)] rounded-xl p-3">
            <p className="text-xs font-bold text-[var(--text-muted)] mb-2">{t('admin.legal.openCase.selectedInstallments')}</p>
            <ul className="space-y-1">
              {installments.map((i, idx) => (
                <li key={idx} className="flex justify-between text-sm">
                  <span className="text-[var(--text-main)]">{i.student_name || i.user_id} #{installmentNo(i) || idx + 1}</span>
                  <span className="font-bold">{fmtTRY(i.amount)}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-[var(--border)] mt-2 pt-2 flex justify-between font-bold text-sm">
              <span>{t('admin.legal.debt.principal')}</span>
              <span className="text-rose-600" data-testid="open-case-total-debt">{fmtTRY(totalDebt)}</span>
            </div>
            <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
              {t('admin.legal.openCase.debtDerivedHint')}
            </p>
          </div>

          {idsMissing && (
            <div
              role="alert"
              data-testid="open-case-id-error"
              className="flex items-start gap-2 rounded-xl border-2 border-rose-300 bg-rose-50 p-3 text-xs font-semibold text-rose-800"
            >
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" aria-hidden />
              {t('admin.legal.openCase.installmentIdMissing')}
            </div>
          )}

          {/* Ek alanlar */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="oc-interest" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                {t('admin.legal.debt.interest')}
              </label>
              <input
                id="oc-interest"
                type="number" min="0" step="0.01"
                value={form.interest_amount}
                onChange={e => setForm(f => ({ ...f, interest_amount: e.target.value }))}
                placeholder="0.00"
                className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
              />
            </div>
            <div>
              <label htmlFor="oc-fees" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                {t('admin.legal.debt.fees')}
              </label>
              <input
                id="oc-fees"
                type="number" min="0" step="0.01"
                value={form.legal_fees}
                onChange={e => setForm(f => ({ ...f, legal_fees: e.target.value }))}
                placeholder="0.00"
                className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
              />
            </div>
          </div>

          <div>
            <label htmlFor="oc-lawyer" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
              {t('admin.legal.col.lawyer')}
            </label>
            <select
              id="oc-lawyer"
              value={form.lawyer_id}
              onChange={e => setForm(f => ({ ...f, lawyer_id: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
            >
              <option value="">{t('admin.legal.openCase.noLawyer')}</option>
              {lawyerOptions.map(l => (
                <option key={lawyerKey(l)} value={lawyerKey(l)}>{l.name}</option>
              ))}
            </select>
            {lawyersDropped > 0 && (
              <p
                role="alert"
                data-testid="open-case-lawyer-id-error"
                className="mt-1.5 flex items-start gap-1.5 text-[11px] font-semibold text-rose-700"
              >
                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" aria-hidden />
                {t('admin.legal.openCase.lawyerIdMissing').replace('{n}', lawyersDropped)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="oc-notes" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
              {t('admin.legal.note.label')}
            </label>
            <textarea
              id="oc-notes"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder={t('admin.legal.note.placeholder')}
              className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2 rounded-xl border border-[var(--border)] text-sm font-bold text-[var(--text-muted)] hover:bg-[var(--bg-muted)] transition-colors">
              {t('common.cancel') || 'İptal'}
            </button>
            <button
              onClick={submit}
              disabled={saving || idsMissing}
              data-testid="open-case-submit"
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-bold disabled:opacity-40 hover:bg-rose-600 transition-colors"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
              {t('admin.legal.btn.openCase')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SEKME 3: Avukatlar
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_LAWYER = {
  name: '',
  firm: '',
  phone: '',
  email: '',
  bar_number: '',
  specialization: '',
  commission_rate: '',
  status: 'active',
};

function LawyersTab({ t, lang, authHeaders }) {
  const toast = useToast();
  const [lawyers, setLawyers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_LAWYER);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const fetchLawyers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/lawyers?skip=${skip}&limit=${limit}`, { headers: authHeaders() });
      setLawyers(res.data.lawyers || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [skip, lang, authHeaders]);

  useEffect(() => { fetchLawyers(); }, [fetchLawyers]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_LAWYER); setShowForm(true); };
  // Kimlik cozulemezse form ACILMAZ: `editing` bos kalinca `save()` PUT yerine
  // POST'a duserdi ve duzenleme yerine KOPYA kayit olusurdu.
  const openEdit = (l) => {
    const id = lawyerKey(l);
    if (!id) {
      toast.error(t('admin.legal.lawyer.idMissing'));
      return;
    }
    setEditing(id);
    setForm({ ...EMPTY_LAWYER, ...l, commission_rate: l.commission_rate ?? '' });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.warning(t('admin.legal.lawyer.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        firm: form.firm || null,
        phone: form.phone || null,
        email: form.email || null,
        bar_number: form.bar_number || null,
        specialization: form.specialization || null,
        commission_rate: form.commission_rate !== '' ? parseFloat(form.commission_rate) : null,
        status: form.status,
      };
      if (editing) {
        await axios.put(`${API}/admin/lawyers/${editing}`, payload, { headers: authHeaders() });
        toast.success(t('admin.legal.msg.lawyerUpdated'));
      } else {
        await axios.post(`${API}/admin/lawyers`, payload, { headers: authHeaders() });
        toast.success(t('admin.legal.msg.lawyerAdded'));
      }
      setShowForm(false);
      setForm(EMPTY_LAWYER);
      setEditing(null);
      fetchLawyers();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(false);
    }
  };

  const deleteLawyer = async (id) => {
    if (!id) return;
    if (!window.confirm(t('admin.legal.lawyer.deleteConfirm'))) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/lawyers/${id}`, { headers: authHeaders() });
      toast.success(t('admin.legal.msg.lawyerDeleted'));
      fetchLawyers();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold hover:opacity-90 transition-opacity"
        >
          <Plus size={16} />
          {t('admin.legal.btn.addLawyer')}
        </motion.button>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-5 mb-5 shadow-sm"
          >
            <h3 className="font-bold text-[var(--text-main)] mb-4">
              {editing ? t('admin.legal.lawyer.editTitle') : t('admin.legal.lawyer.addTitle')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { id: 'lw-name', field: 'name', label: 'admin.legal.lawyer.name', icon: User, required: true },
                { id: 'lw-firm', field: 'firm', label: 'admin.legal.lawyer.firm', icon: Building2 },
                { id: 'lw-phone', field: 'phone', label: 'admin.legal.lawyer.phone', icon: Phone },
                { id: 'lw-email', field: 'email', label: 'admin.legal.lawyer.email', icon: Mail },
                { id: 'lw-bar', field: 'bar_number', label: 'admin.legal.lawyer.barNumber', icon: Hash },
                { id: 'lw-spec', field: 'specialization', label: 'admin.legal.lawyer.specialization', icon: Briefcase },
                { id: 'lw-rate', field: 'commission_rate', label: 'admin.legal.lawyer.commissionRate', icon: Percent, type: 'number' },
              ].map(({ id, field, label, icon: Icon, required, type }) => (
                <div key={field}>
                  <label htmlFor={id} className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                    {t(label)}{required && ' *'}
                  </label>
                  <div className="relative">
                    <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                      id={id}
                      type={type || 'text'}
                      min={type === 'number' ? 0 : undefined}
                      step={type === 'number' ? '0.01' : undefined}
                      value={form[field]}
                      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                      required={required}
                      className="w-full pl-8 pr-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    />
                  </div>
                </div>
              ))}
              <div>
                <label htmlFor="lw-status" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
                  {t('admin.legal.lawyer.status')}
                </label>
                <select
                  id="lw-status"
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                >
                  <option value="active">{t('admin.legal.lawyer.statusActive')}</option>
                  <option value="inactive">{t('admin.legal.lawyer.statusInactive')}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl border border-[var(--border)] text-sm font-bold text-[var(--text-muted)] hover:bg-[var(--bg-muted)] transition-colors">
                {t('common.cancel') || 'İptal'}
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold disabled:opacity-40 hover:opacity-90 transition-opacity"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                {t('common.save') || 'Kaydet'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tablo */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState msg={error} onRetry={fetchLawyers} t={t} />
      ) : lawyers.length === 0 ? (
        <EmptyState icon={Briefcase} title={t('admin.legal.lawyers.empty')} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-[var(--border)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-muted)]">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.lawyer.name')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.lawyer.firm')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.lawyer.phone')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.lawyer.barNumber')}</th>
                  <th className="px-4 py-3 text-right font-bold text-[var(--text-muted)]">{t('admin.legal.lawyer.commissionRate')}</th>
                  <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">{t('admin.legal.lawyer.status')}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {lawyers.map((l, idx) => {
                  const id = lawyerKey(l);
                  const missingId = !id;
                  const missingIdHintId = `lawyer-id-missing-${idx}`;
                  return (
                    <tr key={id || `row-${idx}`} className="bg-[var(--bg-card)] hover:bg-[var(--bg-muted)] transition-colors">
                      <td className="px-4 py-3 font-bold text-[var(--text-main)]">{l.name}</td>
                      <td className="px-4 py-3 text-[var(--text-muted)]">{l.firm || '—'}</td>
                      <td className="px-4 py-3 text-[var(--text-muted)]">{l.phone || '—'}</td>
                      <td className="px-4 py-3 text-[var(--text-muted)] font-mono text-xs">{l.bar_number || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {l.commission_rate != null ? `%${l.commission_rate}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-bold border ${
                          l.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}>
                          {l.status === 'active' ? t('admin.legal.lawyer.statusActive') : t('admin.legal.lawyer.statusInactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {missingId && (
                            <span
                              role="alert"
                              id={missingIdHintId}
                              data-testid="lawyer-id-error"
                              className="flex items-center gap-1 text-[11px] font-semibold text-rose-700"
                            >
                              <AlertTriangle size={13} className="flex-shrink-0" aria-hidden />
                              {t('admin.legal.lawyer.idMissing')}
                            </span>
                          )}
                          <button
                            onClick={() => openEdit(l)}
                            disabled={missingId}
                            data-testid="lawyer-edit-btn"
                            aria-describedby={missingId ? missingIdHintId : undefined}
                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--brand-primary)] hover:bg-[var(--bg-muted)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label={`${t('common.edit') || 'Düzenle'} ${l.name}`}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => deleteLawyer(id)}
                            disabled={deleting === id || missingId}
                            data-testid="lawyer-delete-btn"
                            aria-describedby={missingId ? missingIdHintId : undefined}
                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-rose-500 hover:bg-rose-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label={`${t('common.delete') || 'Sil'} ${l.name}`}
                          >
                            {deleting === id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-[var(--text-muted)]">
            <span>{t('admin.legal.pagination').replace('{from}', skip + 1).replace('{to}', Math.min(skip + limit, total)).replace('{total}', total)}</span>
            <div className="flex gap-2">
              <button disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - limit))} className="px-3 py-1.5 rounded-xl border border-[var(--border)] font-bold disabled:opacity-40 hover:bg-[var(--bg-muted)] transition-colors">{t('admin.legal.prev')}</button>
              <button disabled={skip + limit >= total} onClick={() => setSkip(s => s + limit)} className="px-3 py-1.5 rounded-xl border border-[var(--border)] font-bold disabled:opacity-40 hover:bg-[var(--bg-muted)] transition-colors">{t('admin.legal.next')}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANA SAYFA
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminLegal() {
  const { lang, t } = useLanguage();
  const { authHeaders, user } = useAuth();
  const tr = (trText, enText) => (lang === 'tr' ? trText : enText);
  const [tab, setTab] = useState('cases');

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          rootBreadcrumb(user?.role, tr),
          { label: t('admin.nav.legal') },
        ]}
        title={t('admin.nav.legal')}
        subtitle={t('admin.legal.subtitle')}
      />

      <TabBar tab={tab} setTab={setTab} t={t} />

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          {tab === 'cases' && <CasesTab t={t} lang={lang} authHeaders={authHeaders} />}
          {tab === 'ready' && <ReadyInstallmentsTab t={t} lang={lang} authHeaders={authHeaders} />}
          {tab === 'lawyers' && <LawyersTab t={t} lang={lang} authHeaders={authHeaders} />}
        </motion.div>
      </AnimatePresence>
    </AdminLayout>
  );
}
